import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { toast } from "sonner";
import { getDebate, getUsage, getMe, createChallenge, getDemoDebate } from "@/lib/api";
import { buildCompactShare, copyCompactShare, verdictFromScore, WIN_SCORE, ALMOST_SCORE } from "@/lib/share";
import Header from "@/components/Header";
import ProGateModal from "@/components/ProGateModal";

const TROPHY =
  "https://static.prod-images.emergentagent.com/jobs/b9a6f8ac-ae9e-4cbf-9b6d-e590a64b44ee/images/84e575385d21b0228481c56f1b7c7933db751159096ea6428c884bbea052344f.png";
const AI_AVATAR =
  "https://static.prod-images.emergentagent.com/jobs/b9a6f8ac-ae9e-4cbf-9b6d-e590a64b44ee/images/6c99ee1aa365db3f6f9a5be21aaf930265acd3722b4b50b28b7389055d758d68.png";

function ScoreCountUp({ value, duration = 1.6 }) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v));
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const unsub = rounded.on("change", setDisplay);
    const controls = animate(count, value, { duration, ease: "easeOut" });
    return () => {
      controls.stop();
      unsub();
    };
  }, [value, count, rounded, duration]);

  return <span className="tabular-nums">{display}</span>;
}

function RoundCard({ round, delay }) {
  const b = round.breakdown || {};
  return (
    <motion.div
      initial={{ y: 18, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay, duration: 0.4 }}
      className="bg-white border-4 border-[#101010] rounded-xl cm-shadow p-4 md:p-5"
      data-testid={`round-card-${round.round}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono-cm text-xs uppercase tracking-widest">
          Ronda {round.round}
        </div>
        <div className="font-display text-2xl md:text-3xl tabular-nums">
          {round.score}<span className="text-base text-[#101010]/50">/100</span>
        </div>
      </div>

      <div className="space-y-2.5 mb-3">
        <div>
          <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60 mb-0.5">
            Vos
          </div>
          <p className="text-sm font-medium" data-testid={`round-${round.round}-user`}>
            {round.user_argument}
          </p>
        </div>
        <div>
          <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#FF4C4C] mb-0.5">
            REBECA
          </div>
          <p className="text-sm font-medium italic" data-testid={`round-${round.round}-ai`}>
            {round.ai_counter}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-3 border-t-2 border-[#101010]/10">
        {[
          ["Lóg", b.logica],
          ["Cre", b.creatividad],
          ["Con", b.conviccion],
        ].map(([l, v]) => (
          <div key={l} className="text-center">
            <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60">{l}</div>
            <div className="font-display text-lg tabular-nums">{v}</div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function ResultPage({ user: initialUser, demoMode: demoModeProp = false }) {
  const { debateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const demoMode = demoModeProp || location.state?.demoMode;
  const seoSlug = location.state?.seoSlug || null;
  const [user, setUser] = useState(initialUser || location.state?.user || null);
  const [data, setData] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(!location.state?.result);
  const [revanchaOpen, setRevanchaOpen] = useState(false);
  const [challengeLink, setChallengeLink] = useState(null);
  const [creatingChallenge, setCreatingChallenge] = useState(false);
  // Challenge comparison banner (when result comes from accepting a challenge)
  const incomingChallenge = location.state?.challenge || null;

  useEffect(() => {
    if (data) return;
    (async () => {
      try {
        const d = demoMode ? await getDemoDebate(debateId) : await getDebate(debateId);
        setData(d);
      } catch (_e) {
        toast.error("No se pudo cargar el resultado.");
        navigate(demoMode ? "/demo" : "/game", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [debateId, data, navigate, demoMode]);

  // Revancha trigger: free user, no debates left today, decent score, non-daily debate.
  useEffect(() => {
    if (!data || !user || demoMode) return;
    if (user.is_pro) return;
    if (data.is_daily) return;
    if ((data.total_score ?? 0) < ALMOST_SCORE) return;
    let active = true;
    (async () => {
      try {
        const u = await getUsage();
        if (active && u && !u.is_pro && u.remaining <= 0) {
          // Small delay so user enjoys the score reveal first.
          setTimeout(() => active && setRevanchaOpen(true), 2200);
        }
      } catch (_e) {
        // ignore
      }
    })();
    return () => { active = false; };
  }, [data, user]);

  const onUpgraded = async () => {
    setRevanchaOpen(false);
    try {
      const me = await getMe();
      setUser(me);
    } catch (_e) {
      setUser((u) => (u ? { ...u, is_pro: true } : u));
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="result-loading">
        <div className="font-display text-2xl uppercase">Cargando resultado...</div>
      </div>
    );
  }

  const score = data.total_score ?? 0;
  const verdict = verdictFromScore(score, data.verdict_message);
  const rounds = data.rounds || [];
  const r1 = rounds.find((r) => r.round === 1)?.score ?? 0;
  const r2 = rounds.find((r) => r.round === 2)?.score ?? 0;
  const r3 = rounds.find((r) => r.round === 3)?.score ?? 0;

  const handleCompactShare = async () => {
    try {
      await copyCompactShare({
        score,
        stance: data.stance,
        isDaily: data.is_daily,
        dailyDate: data.daily_date,
      });
      toast.success("Resultado copiado — pegalo en WhatsApp o stories");
    } catch (_e) {
      toast.error("No se pudo copiar el resultado.");
    }
  };

  // ---- Share card (Canvas) — Mejora 1 ----
  const rebecaFinalSass = rounds.find(r => r.round === 3)?.ai_counter || "";
  const handleShare = async () => {
    try {
      const canvas = document.createElement("canvas");
      const W = 1080, H = 1350;
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d");

      const wrap = (text, maxW, font) => {
        ctx.font = font;
        const words = text.split(" ");
        const lines = [];
        let cur = "";
        for (const w of words) {
          const test = cur ? cur + " " + w : w;
          if (ctx.measureText(test).width > maxW && cur) { lines.push(cur); cur = w; }
          else cur = test;
        }
        if (cur) lines.push(cur);
        return lines;
      };

      // BG — color según resultado
      const bgColor = score >= 75 ? "#A8FF76" : score >= 65 ? "#FFDE59" : "#FF4C4C";
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, W, H);

      // Border
      ctx.strokeStyle = "#101010";
      ctx.lineWidth = 20;
      ctx.strokeRect(10, 10, W - 20, H - 20);

      // Header bar
      ctx.fillStyle = "#101010";
      ctx.fillRect(0, 0, W, 120);
      ctx.fillStyle = bgColor;
      ctx.font = "bold 58px Arial";
      ctx.fillText("CONVENCEME", 52, 84);
      ctx.fillStyle = bgColor === "#FF4C4C" ? "#FFDE59" : "#FF4C4C";
      ctx.font = "bold 34px Arial";
      ctx.fillText("vs REBECA", 52, 110);

      // Score gigante
      ctx.fillStyle = "#101010";
      ctx.font = "bold 320px Arial";
      ctx.fillText(String(score), 52, 470);
      ctx.fillStyle = "rgba(16,16,16,0.3)";
      ctx.font = "bold 80px Arial";
      ctx.fillText("/100", 52, 545);

      // Verdict
      ctx.fillStyle = "#101010";
      ctx.font = "bold 54px Arial";
      ctx.fillText(verdict.toUpperCase(), 52, 620);

      // Stance pill
      const stanceText = `"${data.stance}"`;
      const stanceFont = "italic bold 32px Arial";
      const stanceLines = wrap(stanceText, W - 112, stanceFont);
      ctx.font = stanceFont;
      const stancePillH = stanceLines.length * 46 + 32;
      ctx.fillStyle = "#101010";
      roundRect(ctx, 52, 648, W - 104, stancePillH, 16);
      ctx.fill();
      ctx.fillStyle = bgColor;
      stanceLines.forEach((line, i) => ctx.fillText(line, 72, 688 + i * 46));

      // Lo que dijo REBECA — el gancho viral
      const sassY = 648 + stancePillH + 24;
      const sassLabel = score >= 75 ? "REBECA reconoció:" : "REBECA me destrozó:";
      ctx.fillStyle = "#101010";
      ctx.font = "bold 26px Arial";
      ctx.fillText(sassLabel.toUpperCase(), 52, sassY + 36);
      const sassText = `"${rebecaFinalSass.slice(0, 180)}${rebecaFinalSass.length > 180 ? "..." : ""}"`;
      const sassLines = wrap(sassText, W - 112, "italic 28px Arial");
      ctx.font = "italic 28px Arial";
      ctx.fillStyle = "rgba(16,16,16,0.75)";
      sassLines.slice(0, 4).forEach((line, i) => ctx.fillText(line, 52, sassY + 76 + i * 40));

      // Round scores
      const rY = sassY + 76 + Math.min(sassLines.length, 4) * 40 + 28;
      [["R1", r1], ["R2", r2], ["R3", r3]].forEach(([label, val], i) => {
        const x = 52 + i * 330;
        const rColor = val >= 75 ? "#A8FF76" : val >= 65 ? "#FFDE59" : "#FF4C4C";
        ctx.fillStyle = rColor === bgColor ? "#fff" : rColor;
        roundRect(ctx, x, rY, 310, 100, 16);
        ctx.fill();
        ctx.strokeStyle = "#101010";
        ctx.lineWidth = 6;
        roundRect(ctx, x, rY, 310, 100, 16);
        ctx.stroke();
        ctx.fillStyle = "rgba(16,16,16,0.5)";
        ctx.font = "bold 24px Arial";
        ctx.fillText(label, x + 22, rY + 38);
        ctx.fillStyle = "#101010";
        ctx.font = "bold 52px Arial";
        ctx.fillText(String(val), x + 22, rY + 88);
      });

      // CTA bar
      ctx.fillStyle = "#101010";
      ctx.fillRect(0, H - 100, W, 100);
      ctx.fillStyle = "#FFDE59";
      ctx.font = "bold 34px Arial";
      ctx.fillText("¿Podés superarme? → convenceme.app", 52, H - 34);

      // Share or download
      canvas.toBlob(async (blob) => {
        const file = new File([blob], "convenceme.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "Convenceme", text: `Saqué ${score}/100 — ¿podés superarme?` });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "convenceme-resultado.png";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success("¡Imagen guardada!");
        }
      }, "image/png");
    } catch (_e) {
      const text = `Saqué ${score}/100 en Convenceme.app (R1: ${r1} · R2: ${r2} · R3: ${r3}) — ¿podés superarme?`;
      try { await navigator.clipboard.writeText(text); toast.success("¡Copiado!"); }
      catch { toast.error("No se pudo compartir."); }
    }
  };

  // Helper para canvas
  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  // ---- Challenge ----
  // Mejora 2: texto gracioso y contextual según resultado
  const buildShareText = (link) => {
    const sass = rebecaFinalSass ? rebecaFinalSass.slice(0, 90) + (rebecaFinalSass.length > 90 ? "..." : "") : null;
    const stanceShort = data.stance.length > 60 ? data.stance.slice(0, 60) + "..." : data.stance;
    if (score >= WIN_SCORE) {
      return `Le gané a una IA en un debate 🏆 (${score}/100)\n\nDefendí que: "${stanceShort}"${sass ? `\n\nY REBECA tuvo que admitir: "${sass}"` : ""}\n\n¿Podés ganarle vos también? → ${link}`;
    } else if (score >= ALMOST_SCORE) {
      return `Casi le gané a una IA que debate (${score}/100) 😤\n\nEstaba defendiendo que: "${stanceShort}"${sass ? `\n\nY al final me dijo: "${sass}"` : ""}\n\nVos podés hacerlo mejor, seguro → ${link}`;
    } else {
      return `Me destruyó una IA en un debate y no lo puedo creer (${score}/100) 💀\n\nTenía que defender que: "${stanceShort}"${sass ? `\n\nMe dijo: "${sass}"` : ""}\n\nA ver si vos la podés convencer → ${link}`;
    }
  };

  const shareChallengeLink = async (link) => {
    const text = buildShareText(link);
    if (navigator.share) {
      try {
        await navigator.share({ title: "Te desafío en Convenceme", text, url: link });
        return;
      } catch (_e) { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("¡Mensaje copiado! Pegalo en WhatsApp o donde quieras.");
    } catch {
      toast.error("No se pudo copiar.");
    }
  };

  const handleChallenge = async () => {
    if (challengeLink) {
      shareChallengeLink(challengeLink);
      return;
    }
    setCreatingChallenge(true);
    try {
      const ch = await createChallenge(debateId);
      const link = `${window.location.origin}/challenge/${ch.challenge_id}`;
      setChallengeLink(link);
      shareChallengeLink(link);
    } catch (_e) {
      toast.error("No se pudo crear el desafío.");
    } finally {
      setCreatingChallenge(false);
    }
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 md:py-10">
      {demoMode ? (
        <div className="max-w-4xl mx-auto flex items-center justify-between mb-6">
          <Link to="/" className="font-display text-xl md:text-2xl uppercase tracking-tighter">
            Conven<span className="text-[#FF4C4C]">ce</span>me
          </Link>
          <span className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50">Demo</span>
        </div>
      ) : (
        <Header user={user} />
      )}

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto space-y-6"
      >
        {demoMode && (
          <div className="border-4 border-[#101010] rounded-xl cm-shadow bg-[#FFDE59] p-5 md:p-6 text-center">
            <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60 mb-2">
              Modo demo
            </div>
            <p className="font-display uppercase text-xl md:text-2xl leading-tight mb-4">
              {seoSlug
                ? "¿Guardás tu score y seguís con este debate?"
                : "¿Querés guardar tu score y jugar el reto diario?"}
            </p>
            <button
              type="button"
              onClick={() => {
                if (seoSlug) {
                  try {
                    localStorage.setItem("pending_seo_slug", seoSlug);
                  } catch (_e) {}
                }
                window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`;
              }}
              className="cm-press bg-[#101010] text-[#FFDE59] border-4 border-[#101010] rounded-xl px-6 py-3 font-display uppercase text-lg cm-shadow-sm"
            >
              Entrar con Google →
            </button>
          </div>
        )}
        {/* Mejora 6: logros desbloqueados */}
        {data.newly_unlocked_titles?.length > 0 && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
            className="border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden"
          >
            <div className="bg-[#FFDE59] px-5 py-3 flex items-center gap-2">
              <span className="text-xl">🏆</span>
              <span className="font-mono-cm text-[11px] uppercase tracking-widest text-[#101010] font-bold">
                ¡Nuevo logro desbloqueado!
              </span>
            </div>
            <div className="bg-white divide-y-4 divide-[#101010]">
              {data.newly_unlocked_titles.map((t) => (
                <div key={t.id} className="flex items-center gap-4 px-5 py-4">
                  <span className="text-4xl">{t.icon}</span>
                  <div>
                    <div className="font-display uppercase text-lg">{t.label}</div>
                    <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60">{t.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Banner de comparación cuando el usuario aceptó un desafío */}
        {incomingChallenge && (
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden"
          >
            <div className="bg-[#101010] px-5 py-3 font-mono-cm text-[11px] uppercase tracking-widest text-white/70">
              Resultado del desafío
            </div>
            {(incomingChallenge.cached_rounds_count > 0 || incomingChallenge.max_rounds > 1) && (
              <div className="bg-[#A8FF76] px-4 py-2 font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/80 text-center border-b-4 border-[#101010]">
                ⚖️ Misma postura · mismos contraataques de REBECA en las rondas previas
              </div>
            )}
            <div className="grid grid-cols-2 divide-x-4 divide-[#101010]">
              <div className={`p-5 text-center ${score > incomingChallenge.challenger_score ? "bg-[#A8FF76]" : "bg-white"}`}>
                <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60 mb-1">Vos</div>
                <div className="font-display text-5xl tabular-nums">{score}</div>
                {score > incomingChallenge.challenger_score && (
                  <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#2d7a00] mt-1">¡GANASTE! 🏆</div>
                )}
              </div>
              <div className={`p-5 text-center ${incomingChallenge.challenger_score > score ? "bg-[#FF4C4C]/20" : "bg-white"}`}>
                <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60 mb-1">
                  {incomingChallenge.challenger_name?.split(" ")[0]}
                </div>
                <div className="font-display text-5xl tabular-nums">{incomingChallenge.challenger_score}</div>
                {incomingChallenge.challenger_score > score && (
                  <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#FF4C4C] mt-1">GANÓ EL DESAFÍO</div>
                )}
              </div>
            </div>
            {score === incomingChallenge.challenger_score && (
              <div className="bg-[#FFDE59] px-5 py-3 text-center font-display uppercase text-lg">¡Empate! Muy parejo</div>
            )}
          </motion.div>
        )}
        {(() => {
          const won = score >= 75;
          const almostWon = score >= 65;
          const heroBg = won ? "#A8FF76" : almostWon ? "#FFDE59" : "#FF4C4C";
          const heroTextColor = won ? "#101010" : almostWon ? "#101010" : "white";
          return (
            <div
              className="border-4 border-[#101010] rounded-xl cm-shadow p-6 md:p-10 relative overflow-hidden"
              style={{ background: heroBg, color: heroTextColor }}
            >
              {/* Copa si ganaste, REBECA burlándose si perdiste */}
              {won ? (
                <motion.img
                  src={TROPHY}
                  alt="Trofeo"
                  initial={{ rotate: -8, y: -10, opacity: 0 }}
                  animate={{ rotate: -8, y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="hidden md:block absolute -right-6 -top-8 w-48 lg:w-64 drop-shadow-[4px_4px_0px_rgba(16,16,16,1)]"
                />
              ) : (
                <motion.div
                  initial={{ rotate: 8, y: -10, opacity: 0, scale: 0.8 }}
                  animate={{ rotate: 8, y: 0, opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
                  className="hidden md:flex flex-col items-center absolute -right-2 -top-4 w-44 lg:w-52"
                >
                  <img src={AI_AVATAR} alt="REBECA" className="w-full object-contain drop-shadow-[4px_4px_0px_rgba(16,16,16,0.5)]" />
                  <div className={`font-mono-cm text-[10px] uppercase tracking-widest text-center mt-1 px-2 py-1 rounded border-2 border-[#101010] ${almostWon ? "bg-[#101010] text-[#FFDE59]" : "bg-[#101010] text-white"}`}>
                    {almostWon ? "Casi, casi... 😏" : "Jaja, mejor suerte 😂"}
                  </div>
                </motion.div>
              )}

              <div className="font-mono-cm text-xs uppercase tracking-widest mb-2 opacity-70">
                Puntaje total · {data.category_label}
              </div>
              <div className="font-display leading-none">
                <div className="flex items-end gap-2" data-testid="score-value">
                  <span className="text-[5.5rem] sm:text-[7rem] md:text-[10rem] tabular-nums leading-none">
                    <ScoreCountUp value={score} />
                  </span>
                  <span className="text-2xl sm:text-3xl md:text-5xl opacity-50 pb-2 sm:pb-3">/100</span>
                </div>
              </div>
              <div className="font-display uppercase text-2xl md:text-4xl mt-2" data-testid="verdict">
                {verdict}
              </div>

              <div className="grid grid-cols-3 gap-3 mt-6 max-w-md">
                {[["R1", r1], ["R2", r2], ["R3", r3]].map(([l, v]) => (
                  <div key={l} className="bg-white border-4 border-[#101010] rounded-lg px-3 py-2 text-center" data-testid={`round-summary-${l}`}>
                    <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/60">{l}</div>
                    <div className="font-display text-2xl tabular-nums text-[#101010]">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        <div className="bg-[#A8FF76] border-4 border-[#101010] rounded-xl cm-shadow p-5 md:p-7 flex items-start gap-4">
          <img src={AI_AVATAR} alt="REBECA" className="w-14 h-14 md:w-16 md:h-16 shrink-0 object-contain" />
          <div>
            <div className="font-mono-cm text-xs uppercase tracking-widest mb-1">
              <span className="text-[#FF4C4C]">REBECA</span> defendió
            </div>
            <p className="font-display text-lg md:text-2xl uppercase leading-tight" data-testid="result-stance">
              &ldquo;{data.stance}&rdquo;
            </p>
          </div>
        </div>

        {/* Veredicto final de REBECA destacado */}
        {rounds.find(r => r.round === 3)?.ai_counter && (
          <div className="border-4 border-[#101010] rounded-xl cm-shadow bg-[#101010] p-5 md:p-7 flex items-start gap-4">
            <img src={AI_AVATAR} alt="REBECA" className="w-12 h-12 md:w-14 md:h-14 shrink-0 object-contain" />
            <div>
              <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#FF4C4C] mb-2">
                Veredicto final de REBECA
              </div>
              <p className="text-white font-medium text-base md:text-lg leading-relaxed italic">
                &ldquo;{rounds.find(r => r.round === 3).ai_counter}&rdquo;
              </p>
            </div>
          </div>
        )}

        {/* Feedback de REBECA */}
        {data.rebeca_feedback && (
          <div className="border-4 border-[#101010] rounded-xl cm-shadow-sm bg-[#FFDE59] p-4 md:p-5">
            <div className="font-mono-cm text-[10px] uppercase tracking-widest mb-2">💡 Feedback de REBECA</div>
            <p className="text-sm md:text-base font-medium">{data.rebeca_feedback}</p>
          </div>
        )}

        {/* Explicación de puntajes — rediseño */}
        <div className="border-4 border-[#101010] rounded-xl bg-white cm-shadow overflow-hidden">
          <div className="bg-[#101010] px-5 py-3 flex items-center gap-2">
            <span className="font-mono-cm text-[11px] uppercase tracking-widest text-white/70">¿Cómo se puntúa?</span>
            <span className="font-mono-cm text-[11px] text-white/40">— promedio de 3 rondas</span>
          </div>

          {/* Métricas */}
          <div className="grid grid-cols-3 divide-x-4 divide-[#101010] border-b-4 border-[#101010]">
            {[
              ["🧠", "Lógica", "¿Tu argumento tenía fundamento sólido?", "#FFDE59"],
              ["💡", "Creatividad", "¿Fuiste original o dijiste lo obvio?", "#A8FF76"],
              ["🔥", "Convicción", "¿Sonaste seguro o dudoso?", "#C8B4FF"],
            ].map(([icon, name, desc, bg]) => (
              <div key={name} style={{ backgroundColor: bg }} className="p-3 sm:p-4 text-center">
                <div className="text-xl sm:text-2xl mb-1">{icon}</div>
                <div className="font-display text-sm sm:text-base uppercase leading-tight">{name}</div>
                <div className="text-[10px] sm:text-[11px] text-[#101010]/60 leading-snug mt-1 hidden sm:block">{desc}</div>
              </div>
            ))}
          </div>

          {/* Umbrales de victoria */}
          <div className="grid grid-cols-3 divide-x-4 divide-[#101010]">
            <div className="p-3 text-center bg-[#FF4C4C]/10">
              <div className="font-display text-2xl text-[#FF4C4C]">&lt;65</div>
              <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#FF4C4C] mt-0.5">REBECA gana</div>
              <div className="text-[11px] text-[#101010]/50 mt-1">No la convenciste</div>
            </div>
            <div className="p-3 text-center bg-[#FFDE59]/30">
              <div className="font-display text-2xl text-[#7a6000]">65–74</div>
              <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#7a6000] mt-0.5">Casi casi</div>
              <div className="text-[11px] text-[#101010]/50 mt-1">Estuvo cerca</div>
            </div>
            <div className="p-3 text-center bg-[#A8FF76]/40">
              <div className="font-display text-2xl text-[#2d7a00]">75+</div>
              <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#2d7a00] mt-0.5">Vos ganás 🏆</div>
              <div className="text-[11px] text-[#101010]/50 mt-1">La convenciste</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {rounds.map((r, i) => (
            <RoundCard key={r.round} round={r} delay={0.2 + i * 0.15} />
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <button
            onClick={handleCompactShare}
            data-testid="compact-share-button"
            className="cm-press bg-[#FFDE59] border-4 border-[#101010] rounded-xl px-6 py-4 font-display uppercase text-lg cm-shadow flex items-center justify-center gap-2"
          >
            <span>📋</span> Copiar resultado
          </button>

          {/* Compartir imagen */}
          <button
            onClick={handleShare}
            data-testid="share-score-button"
            className="cm-press bg-[#FFDE59] border-4 border-[#101010] rounded-xl px-6 py-4 font-display uppercase text-lg cm-shadow flex items-center justify-center gap-2"
          >
            <span>📸</span> Compartir Resultado
          </button>

          {/* Desafiar amigo */}
          {!demoMode && (
          <button
            onClick={handleChallenge}
            disabled={creatingChallenge}
            data-testid="challenge-button"
            className="cm-press bg-[#A8FF76] border-4 border-[#101010] rounded-xl px-6 py-4 font-display uppercase text-lg cm-shadow flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {creatingChallenge ? (
              "Creando link..."
            ) : challengeLink ? (
              <><span>✅</span> Link copiado — reenviar</>
            ) : (
              <><span>⚔️</span> Desafiá a un amigo</>
            )}
          </button>
          )}

          {/* Nuevo debate */}
          <button
            onClick={() => navigate(demoMode ? "/demo" : "/game", { replace: true })}
            data-testid="new-debate-button"
            className="cm-press bg-[#FF4C4C] text-white border-4 border-[#101010] rounded-xl px-6 py-4 font-display uppercase text-lg cm-shadow"
          >
            {demoMode ? "Jugar otra demo →" : "Elegir Categoría →"}
          </button>

          {/* Ranking */}
          <Link
            to="/leaderboard"
            data-testid="result-leaderboard-link"
            className="cm-press bg-white border-4 border-[#101010] rounded-xl px-6 py-4 font-display uppercase text-lg cm-shadow text-center"
          >
            Ver Ranking
          </Link>
        </div>

        {/* Card del desafío creado */}
        {challengeLink && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="border-4 border-[#101010] rounded-xl overflow-hidden cm-shadow"
          >
            <div className="bg-[#101010] px-4 py-2.5 flex items-center gap-2">
              <span className="text-base">⚔️</span>
              <span className="font-mono-cm text-[11px] uppercase tracking-widest text-white/70">Link del desafío — listo para compartir</span>
            </div>
            <div className="bg-white p-4 space-y-3">
              <div className="font-mono text-xs text-[#101010]/50 break-all bg-[#F0EDE1] border-2 border-[#101010]/10 rounded-lg px-3 py-2">
                {challengeLink}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(buildShareText(challengeLink))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cm-press flex items-center justify-center gap-2 bg-[#25D366] text-white border-4 border-[#101010] rounded-xl px-4 py-3 font-display uppercase text-sm cm-shadow-sm"
                >
                  <span>📱</span> WhatsApp
                </a>
                <button
                  onClick={() => { navigator.clipboard.writeText(challengeLink); toast.success("Link copiado"); }}
                  className="cm-press flex items-center justify-center gap-2 bg-white border-4 border-[#101010] rounded-xl px-4 py-3 font-display uppercase text-sm cm-shadow-sm"
                >
                  <span>🔗</span> Copiar link
                </button>
                <button
                  onClick={() => { navigator.clipboard.writeText(buildShareText(challengeLink)); toast.success("Mensaje copiado"); }}
                  className="cm-press flex items-center justify-center gap-2 bg-[#FFDE59] border-4 border-[#101010] rounded-xl px-4 py-3 font-display uppercase text-sm cm-shadow-sm"
                >
                  <span>📋</span> Copiar mensaje
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {!demoMode && (
        <ProGateModal
          open={revanchaOpen}
          onClose={() => setRevanchaOpen(false)}
          onUpgraded={onUpgraded}
          reason="revancha"
          score={data.total_score}
        />
      )}
    </div>
  );
}
